import { IIntent } from '@lifetools/shared-app-data';
import { initialPendingState } from './initialPendingState';
import { modelsEqual } from './modelsEqual';
import { StoredScheduleBlock } from './StoredScheduleBlock';

/**
 * A committed drop waiting to persist: the model to write, the model it was
 * computed FROM (its base), and the intents whose starts changed.
 */
export interface PendingEntry {
  model: StoredScheduleBlock[];
  base: StoredScheduleBlock[];
  changedIntents: IIntent[];
}

/**
 * The pending-persistence state machine's state. `pending` is the locally
 * authoritative model the schedule renders from (null = render the snapshot);
 * `inFlight` is the entry currently persisting; `queue` holds drops committed
 * behind it; `known` is the chain of models this machine produced (bases and
 * results), used to tell our own snapshot echoes apart from external writes;
 * `lastSnapshot` is the most recent store-derived model observed.
 */
export interface PendingQueueState {
  pending: StoredScheduleBlock[] | null;
  inFlight: PendingEntry | null;
  queue: PendingEntry[];
  known: StoredScheduleBlock[][];
  lastSnapshot: StoredScheduleBlock[] | null;
}

export type PendingQueueEvent =
  | { type: 'drop'; model: StoredScheduleBlock[]; base: StoredScheduleBlock[]; changedIntents: IIntent[] }
  | { type: 'persistResolved' }
  | { type: 'persistFailed' }
  | { type: 'snapshot'; model: StoredScheduleBlock[] }
  | { type: 'timeout' };

export type PendingQueueEffect =
  | { type: 'persist'; entry: PendingEntry }
  | { type: 'abandon'; reason: 'external' | 'failure' | 'timeout' };

export interface PendingQueueTransition {
  state: PendingQueueState;
  effects: PendingQueueEffect[];
}

const isKnown = (known: StoredScheduleBlock[][], model: StoredScheduleBlock[]): boolean =>
  known.some(candidate => modelsEqual(candidate, model));

const cleared = (
  lastSnapshot: StoredScheduleBlock[] | null,
  reason: 'external' | 'failure' | 'timeout',
): PendingQueueTransition => ({
  state: { ...initialPendingState(lastSnapshot) },
  effects: [{ type: 'abandon', reason }],
});

/**
 * The sequential-edit persistence state machine ("edits are always sequential;
 * make updates appear instantaneous").
 *
 * A drop commits its draft model as `pending` synchronously — the schedule
 * renders it immediately and keeps rendering it until the store's snapshot
 * catches up — and enqueues the persist. Persists run strictly one at a time; a
 * drop landing during another's settle window composes on the pending model and
 * queues behind it.
 *
 * External writes are detected by content: any observed snapshot that is NOT a
 * state this machine produced (its `known` chain) means another writer changed
 * the schedule — everything local is abandoned and the snapshot wins. A snapshot
 * matching the pending model settles it (the normal case, near-instant via
 * Firestore's latency-compensated local echo). Persist failure or a settle
 * timeout also abandon, falling back to the last store truth.
 *
 * Pure function: all I/O is returned as effects for the caller to execute.
 * Returns the SAME state reference when nothing changes, so React state wrappers
 * can bail out of re-renders.
 */
export function reconcilePending(
  state: PendingQueueState,
  event: PendingQueueEvent,
): PendingQueueTransition {
  switch (event.type) {
    case 'drop': {
      const entry: PendingEntry = {
        model: event.model,
        base: event.base,
        changedIntents: event.changedIntents,
      };

      return maybeStartPersist({
        ...state,
        pending: event.model,
        queue: [...state.queue, entry],
        known: [...state.known, event.base, event.model],
      });
    }

    case 'persistResolved': {
      if (!state.inFlight) {
        return { state, effects: [] };
      }

      const next = { ...state, inFlight: null };

      // Settled already? The local echo usually lands before the ack; if the store
      // already shows the pending model and nothing else is queued, clear now
      // rather than waiting for another snapshot event.
      if (
        next.queue.length === 0 &&
        next.pending &&
        modelsEqual(next.lastSnapshot, next.pending)
      ) {
        return { state: initialPendingState(next.lastSnapshot), effects: [] };
      }

      return maybeStartPersist(next);
    }

    case 'persistFailed':
      return cleared(state.lastSnapshot, 'failure');

    case 'snapshot': {
      if (modelsEqual(state.lastSnapshot, event.model) && !state.pending) {
        return { state, effects: [] };
      }

      const next = { ...state, lastSnapshot: event.model };

      if (!state.pending) {
        return { state: next, effects: [] };
      }

      // The snapshot matches the pending model and nothing else is queued or in
      // flight: the drop settled — the store is the source of truth again.
      if (!state.inFlight && state.queue.length === 0 && modelsEqual(event.model, state.pending)) {
        return { state: initialPendingState(event.model), effects: [] };
      }

      // A snapshot from our own chain (an echo of an older committed state) is
      // expected while persists are still settling; keep waiting.
      if (isKnown(state.known, event.model) || modelsEqual(event.model, state.pending)) {
        return { state: next, effects: [] };
      }

      // Anything else is an EXTERNAL write: another writer changed the schedule.
      // The snapshot wins — abandon the pending model and every queued drop.
      return cleared(event.model, 'external');
    }

    case 'timeout': {
      // Defensive: the last persist resolved but no matching snapshot ever arrived.
      if (state.pending && !state.inFlight && state.queue.length === 0) {
        return cleared(state.lastSnapshot, 'timeout');
      }
      return { state, effects: [] };
    }

    default:
      return { state, effects: [] };
  }
}

/**
 * Starts the next queued persist when none is in flight. The base guard runs
 * here: the store must currently show a state from our own chain (or the entry's
 * exact base) — anything else means an external write slipped in between commit
 * and pump, and the snapshot wins.
 */
function maybeStartPersist(state: PendingQueueState): PendingQueueTransition {
  if (state.inFlight || state.queue.length === 0) {
    return { state, effects: [] };
  }

  const [entry, ...rest] = state.queue;

  if (
    state.lastSnapshot &&
    !isKnown(state.known, state.lastSnapshot) &&
    !modelsEqual(state.lastSnapshot, entry.base)
  ) {
    return cleared(state.lastSnapshot, 'external');
  }

  return {
    state: { ...state, inFlight: entry, queue: rest },
    effects: [{ type: 'persist', entry }],
  };
}
